import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Home from './pages/Home';
import Cycle from './pages/Cycle';
import Patterns from './pages/Patterns';
import Skin from './pages/Skin';
import Discover from './pages/Discover';
import Medications from './pages/Medications';
import Onboarding from './pages/Onboarding';

export const API_URL = 'https://sylvav2-production.up.railway.app';

const C = {
  bg: '#0D0D12', surface: '#16161F', card: '#1C1C28', border: '#2A2A3A',
  accent: '#E8A2B8', accentSoft: '#C97A9A', teal: '#7ECECA', gold: '#D4AF7A',
  text: '#F0EEF5', muted: '#8A8A9E', danger: '#FF6B7A', green: '#7ECBA5', purple: '#B39DDB',
};

const navItems = [
  { id: 'home', label: 'HOME', icon: '◉' },
  { id: 'cycle', label: 'CYCLE', icon: '◌' },
  { id: 'patterns', label: 'TRENDS', icon: '◈' },
  { id: 'skin', label: 'SKIN', icon: '◇' },
  { id: 'meds', label: 'MEDS', icon: '⊕' },
  { id: 'discover', label: 'DISCOVER', icon: '◎' },
];

export default function App() {
  const [activeTab, setActiveTab] = useState('home');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      axios.get(`${API_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setUser(r.data))
        .catch(() => localStorage.removeItem('authToken'))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  if (loading) return (
    <div style={{ background: '#08080F', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 28, color: C.accent }}>Sylva</div>
    </div>
  );

  if (!user) return <AuthPage setUser={setUser} />;

  if (!user.onboarding_complete) return <Onboarding setUser={setUser} />;

  const renderTab = () => {
    switch (activeTab) {
      case 'home': return <Home user={user} setActiveTab={setActiveTab} />;
      case 'cycle': return <Cycle user={user} />;
      case 'patterns': return <Patterns user={user} />;
      case 'skin': return <Skin user={user} />;
      case 'meds': return <Medications user={user} />;
      case 'discover': return <Discover user={user} />;
      default: return <Home user={user} setActiveTab={setActiveTab} />;
    }
  };

  return (
    <div style={{ background: '#08080F', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '20px 16px', fontFamily: "'Inter',sans-serif" }}>
      <div style={{ width: '100%', maxWidth: 390, background: C.bg, borderRadius: 36, overflow: 'hidden', boxShadow: '0 40px 120px rgba(0,0,0,0.7), 0 0 0 1px #2A2A3A', minHeight: '85vh', display: 'flex', flexDirection: 'column', position: 'relative' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span style={{ fontFamily: "'DM Serif Display',serif", fontSize: 22, color: C.accent }}>Sylva</span>
          </div>
          <button onClick={() => { localStorage.removeItem('authToken'); setUser(null); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: C.muted, fontFamily: 'monospace' }}>
            SIGN OUT
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, padding: '16px 20px 100px', overflowY: 'auto' }}>
          {renderTab()}
        </div>

        {/* Nav */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: `${C.surface}EE`, backdropFilter: 'blur(20px)', borderTop: `1px solid ${C.border}`, padding: '12px 8px 20px', display: 'flex', justifyContent: 'space-around' }}>
          {navItems.map(n => (
            <button key={n.id} onClick={() => setActiveTab(n.id)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '4px 8px' }}>
              <span style={{ fontSize: 18, color: activeTab === n.id ? C.accent : C.muted, transition: 'color 0.2s' }}>{n.icon}</span>
              <span style={{ fontSize: 9, color: activeTab === n.id ? C.accent : C.muted, fontFamily: 'monospace', letterSpacing: '0.06em' }}>{n.label}</span>
              {activeTab === n.id && <div style={{ width: 4, height: 4, borderRadius: '50%', background: C.accent }} />}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function AuthPage({ setUser }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignup, setIsSignup] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const endpoint = isSignup ? '/api/auth/signup' : '/api/auth/login';
      const res = await axios.post(`${API_URL}${endpoint}`, { email, password });
      localStorage.setItem('authToken', res.data.token);
      setUser(res.data.user);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong');
    }
    setLoading(false);
  };

  return (
    <div style={{ background: '#08080F', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20 }}>
      <div style={{ width: '100%', maxWidth: 360, background: '#0D0D12', borderRadius: 28, padding: 32, border: '1px solid #2A2A3A', boxShadow: '0 40px 80px rgba(0,0,0,0.6)' }}>
        <div style={{ marginBottom: 32, textAlign: 'center' }}>
          <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 32, color: '#E8A2B8', marginBottom: 6 }}>Sylva</div>
          <div style={{ fontSize: 12, color: '#8A8A9E', fontFamily: 'monospace' }}>NAVIGATE YOUR HEALTH</div>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required
            style={{ background: '#16161F', border: '1px solid #2A2A3A', borderRadius: 12, padding: '14px 16px', color: '#F0EEF5', fontSize: 14, outline: 'none' }} />
          <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} required
            style={{ background: '#16161F', border: '1px solid #2A2A3A', borderRadius: 12, padding: '14px 16px', color: '#F0EEF5', fontSize: 14, outline: 'none' }} />
          {error && <div style={{ fontSize: 12, color: '#FF6B7A', textAlign: 'center' }}>{error}</div>}
          <button type="submit" disabled={loading}
            style={{ background: '#E8A2B8', color: '#0D0D12', border: 'none', borderRadius: 12, padding: '14px', fontSize: 14, fontWeight: 700, cursor: 'pointer', marginTop: 4 }}>
            {loading ? '...' : isSignup ? 'Create Account' : 'Sign In'}
          </button>
        </form>

        <button onClick={() => setIsSignup(!isSignup)}
          style={{ background: 'none', border: 'none', color: '#8A8A9E', fontSize: 12, cursor: 'pointer', width: '100%', marginTop: 20, fontFamily: 'monospace' }}>
          {isSignup ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
        </button>
      </div>
    </div>
  );
}
